import { computeCaptureFileCountsFromMetadata } from 'components/OrganisationDetails/transactions-tab/entity-transaction-helpers';
import type {
  Organisation,
  ReceiptStatisticsPayload,
} from 'store/organisation/models';
import { isReceiptsEntityProvider } from 'utils/entity-capture-column';
import { findReceiptEntityStats } from 'utils/receipt-statistics';

type ReviewCountEntity = Pick<
  Organisation,
  'uuid' | 'connectionId' | 'providerName' | 'excelFilesMetadata'
>;

type ReceiptStatsInput =
  | Pick<ReceiptStatisticsPayload, 'entitiesStats'>
  | null
  | undefined;

/** Junk, NaN, and negatives become 0. A real zero stays 0. */
const toPositiveFiniteCount = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export type PwaEntityReviewCounts = {
  pendingReview: number;
  readyToPost: number;
};

/**
 * Pending-review and ready-to-post counts for one entity.
 * Resolves receipt stats and capture file metadata once.
 * Pending review matches the home "to review" chip and the Review page segment:
 * capture file metadata when it is loaded, otherwise receipt-statistics `filesPendingReview`.
 * Ready to post uses metadata when loaded, otherwise `filesReviewed`.
 */
export function getPwaEntityReviewCounts(
  entity: ReviewCountEntity,
  receiptStatistics: ReceiptStatsInput
): PwaEntityReviewCounts {
  const stats = findReceiptEntityStats(receiptStatistics?.entitiesStats, {
    connectionId: entity.connectionId,
    organisationId: entity.uuid,
  });
  const fileCountsFromMetadata =
    entity.excelFilesMetadata !== undefined
      ? computeCaptureFileCountsFromMetadata(
          entity.excelFilesMetadata,
          entity.providerName
        )
      : undefined;
  const pendingReview =
    fileCountsFromMetadata?.pendingReviewFiles ??
    stats?.filesPendingReview ??
    0;
  const readyToPost =
    fileCountsFromMetadata?.readyToPostFiles ?? stats?.filesReviewed ?? 0;
  return {
    pendingReview: toPositiveFiniteCount(pendingReview),
    readyToPost: toPositiveFiniteCount(readyToPost),
  };
}

/**
 * Pending-review file count for one entity.
 * Same value as the home entity "to review" chip and the Review page "To review" segment.
 */
export function getPwaEntityPendingReviewCount(
  entity: ReviewCountEntity,
  receiptStatistics: ReceiptStatsInput
): number {
  return getPwaEntityReviewCounts(entity, receiptStatistics).pendingReview;
}

type PendingReviewEntity = Pick<
  Organisation,
  | 'uuid'
  | 'connectionId'
  | 'providerName'
  | 'excelFilesMetadata'
  | 'connectedProperly'
>;

/**
 * Sum of pending-review files across capture entities.
 * Matches the home "to review" chip: disconnected entities contribute 0.
 */
export function getPwaPendingReviewTotal(
  entities: PendingReviewEntity[] | undefined,
  receiptStatistics:
    | Pick<ReceiptStatisticsPayload, 'entitiesStats'>
    | null
    | undefined
): number {
  if (!entities?.length) return 0;
  return entities.reduce((sum, entity) => {
    const needsReconnect =
      !isReceiptsEntityProvider(entity.providerName) &&
      !entity.connectedProperly;
    if (needsReconnect) return sum;
    return sum + getPwaEntityPendingReviewCount(entity, receiptStatistics);
  }, 0);
}
